import React, { memo } from "react";
import styled from "styled-components";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import { config } from "../../config";
import { moduleSettings } from "../../stores/module-settings.store";
import { ConfirmationRouter } from "../confirmation/confirmation-router/confirmation-router";
import { buildRouteWithParams } from "../../helpers/build-route-with-params";
import { ApiErrorCode } from "../../services/api-errors";
import { selectionStore } from "../../stores/selection.store";
import { useTranslation } from "react-i18next";
import { InformationText } from "../information-text/information-text";
import { Commercials } from "../commercials/commercials";
import { GeneralMessage } from "../general-message/general-message";
import { Selection } from "../selection/selection-criteria/selection";
import { ScrollToTop } from "../selection/scroll-to-top/scroll-to-top";
import { WaitingMessage } from "../waiting-message/waiting-message";
import * as Sentry from "@sentry/react";
import { ErrorType } from "../../services/api-errors/error-types";
import { requestParamsImpl } from "../../services/request-params.service";

export interface IStyle {
  isUpdateFromBooking?: boolean | false;
}

export const MainContentFlex = styled.div<IStyle>`
  display: flex;
  flex-direction: column;
  min-height: ${(props) =>
    props.isUpdateFromBooking
      ? `calc(
    100vh -
    ${
      parseInt(props.theme.headerHeight, 0) +
      parseInt(props.theme.footerHeight, 0)
    }px - 62px)}`
      : `calc(
    100vh -
    ${
      parseInt(props.theme.headerHeight, 0) +
      parseInt(props.theme.footerHeight, 0)
    }px - 32px)}`};
  max-width: 910px;
  color: ${(props) => props.theme.textColor};
  margin: 10px auto 0;
  padding-bottom: 48px;

  @media screen and (max-width: 930px) {
    max-width: 100%;
    padding-bottom: 0;
  }

  @media screen and (max-width: 780px) {
    margin-bottom: 0;
  }
`;

const MainContentWrapper = styled.div`
  display: flex;
  flex: 1 1 100%;
`;

const MainContentLayout = styled.div`
  width: 100%;

  @media screen and (max-width: 980px) {
    padding: 0 12px;
  }

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const MainContentDef = () => {
  const { t } = useTranslation();
  const onlineBookingEnabled = moduleSettings.onlineBookingEnabled;
  const isUpdateFromBooking = moduleSettings.isUpdateFromBooking;
  const hasServices = moduleSettings.periods.length > 0;

  const areaNoMoreAvailaible =
    moduleSettings.updateFromBooking.area_id &&
    !moduleSettings.areaIds.includes(
      Number(moduleSettings.updateFromBooking.area_id)
    );

  const isRefreshOnPayment =
    localStorage.getItem("pendingBookingId") &&
    localStorage.getItem("referralKey") === moduleSettings.getReferralKey();

  const ErrorFallback = ({
    error,
    resetError,
  }: {
    error: any;
    resetError: any;
  }) => {
    if (error.code === ApiErrorCode.pending_booking_unfound) {
      selectionStore.resetStore();
    }

    resetError();

    return (
      <Redirect
        to={{
          pathname:
            typeof error.getRedirectPath === "function"
              ? error.getRedirectPath()
              : buildRouteWithParams("selection", false),
          search: requestParamsImpl.computeSearchQueryForOverwrittenParams(),
          state: {
            error:
              typeof error.getMessage === "function"
                ? error.getMessage()
                : t("instabook.errors.error"),
            errorType:
              typeof error.getErrorType === "function"
                ? error.getErrorType()
                : ErrorType.error,
            errorDetails: error.data,
          },
        }}
      />
    );
  };

  const mainContent =
    !onlineBookingEnabled || !hasServices ? (
      <InformationText
        key={"no_online_booking_enabled_msg"}
        text={t("instabook.errors.no_access")}
        status={"warning"}
      />
    ) : (
      <>
        {areaNoMoreAvailaible && (
          <InformationText
            key={"area_no_more_availaible"}
            text={t("instabook.errors.area_no_more_availaible")}
            status={"warning"}
          />
        )}
        <Switch>
          <ScrollToTop>
            <Sentry.ErrorBoundary fallback={ErrorFallback}>
              <GeneralMessage />
              <WaitingMessage />
              <Route
                path={buildRouteWithParams(config.routes.selection, false)}
              >
                <Selection />
              </Route>
              <Route
                path={buildRouteWithParams(config.routes.commercials, false)}
              >
                <Commercials />
              </Route>
              <Route
                path={buildRouteWithParams(config.routes.confirmation, false)}
              >
                <ConfirmationRouter />
              </Route>
            </Sentry.ErrorBoundary>
          </ScrollToTop>
        </Switch>
      </>
    );

  return (
    <MainContentFlex isUpdateFromBooking={isUpdateFromBooking}>
      <Router>
        <MainContentWrapper>
          <MainContentLayout>{mainContent}</MainContentLayout>
          <Redirect
            to={buildRouteWithParams(
              isRefreshOnPayment
                ? config.routes.confirmationOnRefresh
                : config.routes.selection
            )}
          />
        </MainContentWrapper>
      </Router>
    </MainContentFlex>
  );
};

export const MainContent = memo(MainContentDef);
